// Prévenir à temps d'une absence, le désistement tardif et l'absence sans prévenir (D-148, maquettes a0 à e4 validées) :
//  - la ⚙ de la page de l'équipe (D-139) : « Prévenir d’une absence » (chaque semaine, ou un délai avant chaque rendez-vous ;
//    l'aperçu de ce que lisent les joueuses) et « Désistements tardifs » (le seuil de « Faire le point », l'explication
//    après une absence sans prévenir) ;
//  - la feuille d'absence : avant la limite, « À temps, merci ! » ; après, la feuille du désistement tardif (merci, pourquoi
//    ça compte, la raison d'un toucher, un mot, « Prévenir le coach ») ;
//  - l'accueil de la joueuse et du parent : « Tout va bien ? » (la réponse d'un toucher, un mot), « Ta semaine » (de la
//    veille de la limite à la limite), « Prévenu tard » sur la vedette ;
//  - l'accueil du coach : « Faire le point avec … ? » ; la séance : « prévenu tard », « sans prévenir », la raison, le mot,
//    « Ne pas compter », « Ne pas lui écrire » ;
//  - le bureau : le taux de « prévenu à temps » par équipe, rien de nominatif.
import './desist.css';
import {
  absenceAnswer, absenceClubRates, absenceDeadline, absenceDeclare, absenceExcuse, absenceMine, absencePointDone, absencePoints, absenceQuiet,
  absenceSettings, absenceSettingsSet, type AbsenceSettings, type AbsenceSettingsDraft, type BoardNotice, type Deadline, type MyAsk, type MyWeek,
  type PointCard, type WeekItem,
} from '../lib/desist-api';
import { occurrenceException, setResponse } from '../lib/api';
import { personContacts } from '../lib/reach-api';
import { ASK_REASONS, LATE_REASONS, limitTimes, askCopy, coachTold, lateCopy, lateSummary, lateTag, limitSummary, ordinal, pointLead, reasonLabel, type ReasonKey } from '../domain/desist';
import { agree, playerNoun, type TeamGender } from '../domain/labels';
import { absenceExample, isMinorPerson } from '../domain/minors';
import { parseLocalDate } from '../domain/schedule';
import { teamOf, type Ctx } from './context';
import { $, $$, html, mount, openSheet, toast, type Html } from './dom';
import { settingsPage, type Rubrique } from './nav';
import { contactsBlock } from './team';

const WD = ['L', 'M', 'M', 'J', 'V', 'S', 'D'];
const WD_LONG = ['lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi', 'dimanche'];
/** « nouveau » à côté de la rubrique, le temps de la découvrir. */
const NEW_UNTIL = '2026-12-31';
const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
const hm = (iso: string) => { const d = new Date(iso); return `${d.getHours()}h${String(d.getMinutes()).padStart(2, '0')}`; };
const dateTile = (date: string) => {
  const d = parseLocalDate(date);
  return html`<span class="date-tile"><b>${d.getDate()}</b><small>${d.toLocaleDateString('fr-FR', { month: 'short' })}</small></span>`;
};
const wdShort = (date: string) => cap(parseLocalDate(date).toLocaleDateString('fr-FR', { weekday: 'short' }));

// =================================================================================================================
// La ⚙ de l'équipe : #/t/<équipe>/reglages
// =================================================================================================================
export async function renderTeamSettings(root: HTMLElement, ctx: Ctx, teamId: string, rubric?: string): Promise<void> {
  const team = teamOf(ctx, teamId);
  if (!team || !ctx.staffOf.has(teamId)) {
    mount(root, html`<section class="screen"><a class="back" href="#/t/${teamId}">‹ Équipe</a>
      <div class="empty"><p class="empty-title">Réservé au staff de l’équipe.</p></div></section>`);
    return;
  }
  const s = await absenceSettings(teamId).catch(() => null);
  if (!s) {
    mount(root, html`<section class="screen"><a class="back" href="#/t/${teamId}">‹ ${team.name}</a>
      <div class="empty"><p class="empty-title">Chargement impossible.</p><p>Vérifie ta connexion puis réessaie.</p></div></section>`);
    return;
  }
  const g = team.gender;
  // D-188 : « Qui lave les maillots ? », au coach de l'équipe seulement (le bureau ne le voit pas).
  const jerseys = ctx.full.pureCoach.has(teamId) ? await import('./maillots').then(m => m.jerseysSummary(teamId)).catch(() => null) : null;
  const draft: AbsenceSettingsDraft = { mode: s.mode, weekday: s.weekday, at_time: s.at_time, training_h: s.training_h, match_h: s.match_h, threshold: s.threshold, ask_no_show: s.ask_no_show };
  const fresh = new Date().toISOString().slice(0, 10) <= NEW_UNTIL;
  const save = async (patch: Partial<AbsenceSettingsDraft>) => {
    try { await absenceSettingsSet(teamId, { ...draft, ...patch }); toast('C’est réglé'); await renderTeamSettings(root, ctx, teamId, rubric); }
    catch { toast('Enregistrement impossible.', 'ko'); }
  };
  const rubrics: Rubrique[] = [
    { key: 'absence', title: html`Prévenir d’une absence${fresh ? html`<span class="ds-new">nouveau</span>` : ''}`, sum: limitSummary(draft), icon: 'ti-cal', guide: 'ds-set-limit',
      body: (el) => {
        mount(el, limitBody(s, g));
        for (const b of $$<HTMLButtonElement>(el, '[data-mode]')) b.addEventListener('click', () => { if (b.dataset.mode !== draft.mode) void save({ mode: b.dataset.mode as 'weekly' | 'delay' }); });
        for (const b of $$<HTMLButtonElement>(el, '[data-wd]')) b.addEventListener('click', () => void save({ weekday: Number(b.dataset.wd) }));
        $<HTMLSelectElement>(el, '#ds-time')?.addEventListener('change', (e) => void save({ at_time: (e.target as HTMLSelectElement).value }));
        for (const b of $$<HTMLButtonElement>(el, '[data-th]')) b.addEventListener('click', () => void save({ training_h: Number(b.dataset.th) as 12 | 24 | 48 }));
        for (const b of $$<HTMLButtonElement>(el, '[data-mh]')) b.addEventListener('click', () => void save({ match_h: Number(b.dataset.mh) as 24 | 48 | 72 }));
      } },
    { key: 'desistements', title: 'Désistements tardifs', sum: lateSummary(draft.threshold, draft.ask_no_show), icon: 'ti-hand', guide: 'ds-set-late',
      body: (el) => {
        mount(el, html`
          <div class="ds-row" data-guide="ds-set-threshold"><p class="ds-lbl">Une carte « Faire le point avec … ? » sur ton accueil au</p>
            <div class="ds-hours" role="group" aria-label="Le seuil">${[2, 3, 4].map(n => html`<button type="button" class="chip-toggle" data-thr="${n}" aria-pressed="${String(draft.threshold === n)}">${ordinal(n)}</button>`)}</div>
            <p class="hint">désistement tardif de la saison. Aucune sanction automatique : c’est toi qui vois.</p></div>
          <div class="ds-on"><span>Demander une explication après une absence sans prévenir<small>Le message part à la fin de la séance ; pour ${agree(g, 'un mineur', 'une mineure')}, aussi à ses parents.</small></span>
            <button type="button" class="iv-switch" role="switch" id="ds-ask" aria-checked="${String(draft.ask_no_show)}" aria-label="Demander une explication après une absence sans prévenir"></button></div>
          <p class="ds-def"><b>« ${agree(g, 'Malade ou blessé', 'Malade ou blessée')} » ne compte jamais.</b> Sur la fiche de séance, « Ne pas compter » retire un désistement du compte.</p>`);
        for (const b of $$<HTMLButtonElement>(el, '[data-thr]')) b.addEventListener('click', () => void save({ threshold: Number(b.dataset.thr) as 2 | 3 | 4 }));
        $(el, '#ds-ask')?.addEventListener('click', () => void save({ ask_no_show: !draft.ask_no_show }));
      } },
    ...(jerseys ? [{ key: 'maillots', title: 'Qui lave les maillots ?', sum: jerseys, icon: 'ti-bag', guide: 'lm-settings',
      body: (el: HTMLElement) => void import('./maillots').then(m => m.renderJerseysSettings(el, ctx, teamId)) }] : []),
  ];
  settingsPage(root, {
    key: 'equipe', info: { href: `#/t/${teamId}/reglages`, title: 'Réglages de l’équipe', eyebrow: team.name, parent: { hash: `#/t/${teamId}`, label: team.name } },
    intro: 'Ce qui vaut pour toute l’équipe.', rubrics, open: rubric ?? null, openByDefault: ['absence'], guide: 'ds-settings',
  }, () => renderTeamSettings(root, ctx, teamId, rubric));
}

function limitBody(s: AbsenceSettings, g: TeamGender): Html {
  const weekly = s.mode === 'weekly';
  const chips = (vals: number[], on: number, attr: 'th' | 'mh', label: string) => html`<div class="ds-hours" role="group" aria-label="${label}">${vals.map(v => attr === 'th'
    ? html`<button type="button" class="chip-toggle" data-th="${v}" aria-pressed="${String(on === v)}">${v} h</button>`
    : html`<button type="button" class="chip-toggle" data-mh="${v}" aria-pressed="${String(on === v)}">${v} h</button>`)}</div>`;
  return html`
    <p class="nv-intro hint">Jusqu’à la limite, ${agree(g, 'chacun', 'chacune')} se dit ${agree(g, 'absent', 'absente')} librement. Après, ${agree(g, 'il', 'elle')} le peut toujours : l’app lui demande pourquoi et te prévient.</p>
    <div class="seg" role="group" aria-label="La limite" data-guide="ds-set-mode">
      <button type="button" data-mode="weekly" aria-pressed="${String(weekly)}">Chaque semaine</button>
      <button type="button" data-mode="delay" aria-pressed="${String(!weekly)}">Avant chaque rendez-vous</button>
    </div>
    ${weekly ? html`
      <div class="ds-row"><p class="ds-lbl">Le jour</p>
        <div class="ix-days ds-days7" role="group" aria-label="Le jour">${WD.map((d, i) => html`<button type="button" class="chip-toggle" data-wd="${i + 1}" aria-pressed="${String(s.weekday === i + 1)}" aria-label="${WD_LONG[i]}">${d}</button>`)}</div></div>
      <div class="ds-row2"><div class="ds-row"><p class="ds-lbl">L’heure</p><p class="hint">${s.preview.span}</p></div>
        <label class="ds-sel"><select id="ds-time" aria-label="L’heure">${limitTimes(s.at_time).map(t => html`<option value="${t}" ${t === s.at_time ? 'selected' : ''}>${t.replace(':', 'h')}</option>`)}</select></label></div>`
    : html`
      <div class="ds-row"><p class="ds-lbl">Avant une séance</p>${chips([12, 24, 48], s.training_h, 'th', 'Avant une séance')}</div>
      <div class="ds-row"><p class="ds-lbl">Avant un match</p>${chips([24, 48, 72], s.match_h, 'mh', 'Avant un match')}
        <p class="hint">À l’extérieur : compté depuis l’heure du rendez-vous de la convocation.</p></div>`}
    <div class="ds-prev" data-guide="ds-set-preview"><p class="eyebrow">Ce que lisent les ${playerNoun(g, 2)}</p>
      <div class="ds-pn"><span class="nt-ico nt-reminder" aria-hidden="true">⏰</span><div><b>${s.preview.remind}</b>
        <p>${s.preview.text ?? 'Pas de rendez-vous cette semaine-là : pas de rappel.'}</p></div></div>
      ${!weekly && s.preview.items.length ? html`<ul class="ds-dl">${s.preview.items.map(i => html`<li><span>${i.label}</span><b>${i.limit}</b></li>`)}</ul>` : ''}
      <p class="hint">Les jours tiennent compte des indisponibilités de chacune (chaque semaine, périodes). Après la limite, on peut toujours se dire ${agree(g, 'absent', 'absente')} : c’est un désistement tardif, ${s.viewer_g ? `tu es ${s.viewer_g === 'F' ? 'prévenue' : 'prévenu'} tout de suite` : 'tu le sais tout de suite'}.</p>
    </div>`;
}

// =================================================================================================================
// La feuille d'absence : à temps, ou le désistement tardif
// =================================================================================================================
export interface AbsenceTarget {
  eventId: string; date: string; personId: string; name: string; isMe: boolean; gender: TeamGender; minor: boolean;
  kind: string; start: Date;
}

/** Se dire absente (ou dire l'absence de son enfant) : avant la limite, la feuille d'avant avec « À temps, merci ! » ;
 *  après, la feuille du désistement tardif. Rien de bloqué. */
export async function openAbsence(t: AbsenceTarget, onDone: () => void | Promise<void>): Promise<void> {
  const dl = await absenceDeadline(t.eventId, t.date, t.personId);
  if (dl?.late) { openLateSheet(t, dl, onDone); return; }
  const day = `${parseLocalDate(t.date).toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' })}`;
  openSheet(t.isMe ? 'Prévenir de ton absence' : `Absence de ${t.name}`, html`
    <p class="ds-ctx">${day} · ${t.kind === 'match' ? 'match' : t.kind === 'training' ? 'entraînement' : 'rendez-vous'}</p>
    <form class="form" id="f-abs" novalidate>
      <label class="field"><span>Motif (facultatif, vu par le staff)</span><input name="reason" maxlength="140" placeholder="${absenceExample(t.minor)}"></label>
      ${dl ? html`<p class="ds-ontime" data-guide="ds-ontime"><span class="ti ti-check" aria-hidden="true"></span>À temps : tu préviens avant ${dl.label}. Merci !</p>` : ''}
      <button class="btn btn-primary" type="submit">Confirmer l’absence</button>
    </form>`, (sheet, close) => {
    $<HTMLFormElement>(sheet, '#f-abs')!.addEventListener('submit', async (e) => {
      e.preventDefault();
      const reason = String(new FormData(e.target as HTMLFormElement).get('reason') ?? '');
      try { await setResponse(t.eventId, t.date, t.personId, 'absent', reason); close(); toast('Absence notée, le coach est prévenu'); await onDone(); }
      catch { toast('Enregistrement impossible.', 'ko'); }
    });
  });
}

/** La feuille du désistement tardif (maquette c2) : merci, pourquoi ça compte, la raison d'un toucher, un mot. */
export function openLateSheet(t: AbsenceTarget, dl: Deadline, onDone: () => void | Promise<void>): void {
  const pg = dl.g ?? t.gender;   // la personne : sa fiche, sinon son équipe
  const c = lateCopy({ kind: dl.kind, soon: dl.soon, hm: hm(dl.starts), limit: dl.label, coach: dl.coach, g: t.gender, pg, self: t.isMe, name: t.name });
  let reason: ReasonKey | null = null;
  openSheet(t.isMe ? 'Prévenir de ton absence' : `Absence de ${t.name}`, html`
    <div class="ds-late-sheet" data-guide="ds-late-sheet">
      <p class="ds-ctx">${c.context}</p>
      <div class="ds-kind"><span class="ds-k-ic" aria-hidden="true">🏀</span><div><p><b>${c.thanks}</b> ${c.limit}</p><p>${c.why}</p></div></div>
      <div class="ds-row"><p class="ds-lbl">${c.question}</p>
        <div class="chip-toggles ds-reasons" role="group" aria-label="${c.question}">${LATE_REASONS.map(k => html`
          <button type="button" class="chip-toggle" data-reason="${k}" aria-pressed="false">${reasonLabel(k, pg, { minor: t.minor })}</button>`)}</div></div>
      <label class="field"><span>Un mot pour ${dl.coach ?? 'le coach'} (facultatif)</span><input name="word" maxlength="80" placeholder="${t.minor ? 'ex. sortie scolaire qui finit tard' : 'ex. réunion qui finit tard'}"></label>
      <p class="form-msg" role="alert"></p>
      <button class="btn btn-primary" type="button" id="ds-send">Prévenir le coach</button>
      <p class="ds-foot">${c.foot}</p>
    </div>`, (sheet, close) => {
    for (const b of $$<HTMLButtonElement>(sheet, '[data-reason]')) b.addEventListener('click', () => {
      reason = b.dataset.reason as ReasonKey;
      $$(sheet, '[data-reason]').forEach(x => x.setAttribute('aria-pressed', String(x === b)));
      $(sheet, '.form-msg')!.textContent = '';
    });
    $(sheet, '#ds-send')!.addEventListener('click', async () => {
      if (!reason) { $(sheet, '.form-msg')!.textContent = t.isMe ? 'Choisis ce qui t’empêche de venir.' : `Choisis ce qui empêche ${t.name} de venir.`; return; }
      const word = $<HTMLInputElement>(sheet, 'input[name=word]')!.value.trim() || null;
      try {
        await absenceDeclare(t.eventId, t.date, t.personId, reason, word);
        close();
        toast(`${coachTold(dl.coach, dl.coach_g)} Merci d’avoir dit pourquoi.`);
        await onDone();
      } catch { toast('Enregistrement impossible.', 'ko'); }
    });
  });
}

// =================================================================================================================
// L'accueil de la joueuse et du parent
// =================================================================================================================
/** « Tout va bien ? » puis « Ta semaine », en tête de l'accueil ; « Prévenu tard » sur la vedette (`home`). D-155 : la vedette
 *  de l'accueil est la semaine elle-même (`weeks: false`, la carte « Ta semaine » n'y est plus). */
export async function renderHomeDesist(el: HTMLElement, ctx: Ctx, home: HTMLElement, reload: () => void, opts: { weeks?: boolean } = {}): Promise<void> {
  const mine = await absenceMine();
  if (opts.weeks === false) mine.weeks = [];
  mount(el, html`${mine.asks.map(a => askCard(a))}${mine.weeks.map(w => weekCard(w))}`);
  // La vedette d'une séance où l'on s'est dit absente après la limite (maquette c3).
  for (const l of mine.lates) {
    const hero = $<HTMLElement>(home, `[data-ds-occ="${l.event_id}/${l.date}"][data-ds-pid="${l.person_id}"]`);
    if (!hero || $(hero, '.ds-hero-note')) continue;
    hero.insertAdjacentHTML('beforeend', html`<p class="ds-hero-note">${cap(lateTag(l.pgender))}${l.reason_label ? ` · ${l.reason_label}` : ''} · ${l.coach ?? 'Le coach'} a ton message.</p>`.s);
  }
  wireAsks(el, mine.asks, () => void renderHomeDesist(el, ctx, home, reload));
  wireWeeks(el, ctx, mine.weeks, reload);
}

function askCard(a: MyAsk): Html {
  const c = askCopy({ self: a.self, name: a.name, coach: a.coach, kind: a.kind, soon: a.soon, hm: a.hm, label: a.label, team: a.team.replace(/\s+(filles|garçons|gars|mixte)$/i, ''),
    minor: a.minor, hasAccount: a.has_account, g: a.gender });
  const answered = !!a.reason;
  const sent = !answered ? '' : a.by_me ? `Envoyé à ${a.coach ?? 'le coach'} : ${a.reason_label}` : `${a.answered_by ?? 'Réponse'} a répondu : ${a.reason_label}`;
  return html`
    <section class="card ds-ask" data-guide="ds-ask" data-ask="${a.id}">
      <span class="eyebrow eyebrow-accent">${c.eyebrow}</span>
      <span class="card-title">${c.title}</span>
      <p>${c.text}</p>
      ${answered ? html`<p class="ds-sent"><span class="ti ti-check" aria-hidden="true"></span>${sent}</p>`
        : html`<div class="chip-toggles ds-reasons" role="group" aria-label="Ce qui s’est passé">${ASK_REASONS.map(k => html`
            <button type="button" class="chip-toggle" data-ask-reason="${k}" aria-pressed="false">${reasonLabel(k, a.pgender ?? a.gender, { minor: a.minor, self: a.self })}</button>`)}</div>`}
      <label class="field"><span>${answered ? 'Ajouter un mot (facultatif)' : c.field}</span><input data-ask-word maxlength="80" value="${a.word ?? ''}" placeholder="${c.placeholder}"></label>
      ${answered ? html`<button type="button" class="btn btn-primary btn-sm" data-ask-send>Envoyer le mot</button>` : ''}
      ${c.both ? html`<p class="ds-both"><span class="ti ti-people" aria-hidden="true"></span>${c.foot}</p>` : html`<p class="hint">${c.foot}</p>`}
    </section>`;
}

function wireAsks(el: HTMLElement, asks: MyAsk[], again: () => void): void {
  for (const card of $$<HTMLElement>(el, '[data-ask]')) {
    const a = asks.find(x => x.id === card.dataset.ask);
    if (!a) continue;
    const word = () => $<HTMLInputElement>(card, '[data-ask-word]')!.value.trim();
    for (const b of $$<HTMLButtonElement>(card, '[data-ask-reason]')) b.addEventListener('click', async () => {
      b.setAttribute('aria-pressed', 'true');
      try { await absenceAnswer(a.id, b.dataset.askReason as ReasonKey, word() || null); again(); }
      catch { toast('Envoi impossible. Réessaie.', 'ko'); b.setAttribute('aria-pressed', 'false'); }
    });
    $(card, '[data-ask-send]')?.addEventListener('click', async () => {
      try { await absenceAnswer(a.id, null, word()); toast(`Mot envoyé à ${a.coach ?? 'au coach'}`); again(); }
      catch { toast('Envoi impossible. Réessaie.', 'ko'); }
    });
  }
}

/** « Tu es attendue vendredi et samedi … » : les jours en gras (maquette b2). */
const weekText = (t: string | null): Html => {
  if (!t) return html``;
  const m = /^(.*? est|Tu es) (attendu|attendue) (.+?)( \(.*|\. Un empêchement.*)$/.exec(t);
  return m ? html`${m[1]} ${m[2]} <b>${m[3]}</b>${m[4]}` : html`${t}`;
};

function itemWhat(i: WeekItem): string {
  if (i.kind !== 'match') return i.title || 'Entraînement';
  return i.opponent ? `Match ${i.is_home === false ? 'à' : 'contre'} ${i.opponent}` : 'Match';
}

function weekCard(w: MyWeek): Html {
  const g = w.pgender ?? w.gender;   // la personne : sa fiche, sinon son équipe
  const due = w.mode === 'weekly' && w.limit_label ? `avant ${w.limit_label}` : `${w.training_h} h avant une séance`;
  const rows = w.items.map(i => {
    const wd = WD_LONG[(parseLocalDate(i.date).getDay() + 6) % 7]!;
    const their = w.self ? 'ta' : 'sa';
    const st = i.status === 'absent' && i.source === 'rule' ? html`<span class="ds-ko">${agree(g, 'Indiqué absent', 'Indiquée absente')} (${their} règle du ${wd})</span>`
      : i.status === 'absent' && i.source === 'period' ? html`<span class="ds-ko">Indisponible : ${agree(g, 'noté absent', 'notée absente')}</span>`
      : i.status === 'absent' ? html`<span class="ds-ko">${agree(g, 'Absent', 'Absente')}${i.reason ? ` · « ${i.reason} »` : ''}</span>`
      : i.status === 'uncertain' ? html`<span class="ds-unc">? ${agree(g, 'Incertain', 'Incertaine')}</span>`
      : html`<span class="ds-ok">✓ ${agree(g, 'Attendu', 'Attendue')}</span>`;
    const act = i.status === 'absent' && (i.source === 'rule' || i.source === 'period')
      ? html`<button type="button" class="btn btn-ghost btn-sm" data-week-exc="${i.event_id}/${i.date}" data-pid="${w.person_id}">${w.self ? 'Je viens' : `${agree(g, 'Il', 'Elle')} vient`}</button>`
      : i.status === 'absent' ? html`<button type="button" class="btn btn-ghost btn-sm" data-week-back="${i.event_id}/${i.date}" data-pid="${w.person_id}">${w.self ? 'Je viens' : `${agree(g, 'Il', 'Elle')} vient`}</button>`
      : html`<button type="button" class="btn btn-ghost btn-sm" data-week-abs="${i.event_id}/${i.date}" data-pid="${w.person_id}" data-kind="${i.kind}" data-starts="${i.starts}">${agree(g, 'Absent ?', 'Absente ?')}</button>`;
    return html`<li>${dateTile(i.date)}<span class="card-main"><span class="ds-day-t">${wdShort(i.date)} · ${itemWhat(i)}</span>
      <span class="muted small">${hm(i.starts)}${i.location ? ` · ${i.location}` : ''}${w.mode === 'delay' ? ` · avant ${i.deadline_label}` : ''}</span>${st}</span>${act}</li>`;
  });
  return html`
    <section class="card ds-week" data-guide="ds-week">
      <div class="ds-week-h"><span class="eyebrow eyebrow-accent">${w.self ? 'Ta semaine' : `${w.name} · sa semaine`}</span>
        <span class="ds-due"><span class="ti ti-chrono" aria-hidden="true"></span>${due}</span></div>
      <p class="ds-week-t">${weekText(w.text)}</p>
      <ul class="ds-days">${rows}</ul>
      ${w.self ? html`<p class="ds-skin"><span>Préviens à temps 4 semaines de suite : un skin à débloquer.</span><span class="chip chip-soft">bientôt</span></p>` : ''}
    </section>`;
}

function wireWeeks(el: HTMLElement, ctx: Ctx, weeks: MyWeek[], reload: () => void): void {
  const split = (s: string) => s.split('/') as [string, string];
  for (const b of $$<HTMLButtonElement>(el, '[data-week-abs]')) b.addEventListener('click', () => {
    const [ev, date] = split(b.dataset.weekAbs!);
    const w = weeks.find(x => x.person_id === b.dataset.pid)!;
    const p = ctx.people.find(x => x.id === w.person_id);
    void openAbsence({ eventId: ev, date, personId: w.person_id, name: w.name, isMe: w.self, gender: w.gender, minor: isMinorPerson(p), kind: b.dataset.kind ?? 'training',
      start: new Date(b.dataset.starts ?? `${date}T12:00:00`) }, reload);
  });
  for (const b of $$<HTMLButtonElement>(el, '[data-week-exc]')) b.addEventListener('click', async () => {
    const [ev, date] = split(b.dataset.weekExc!);
    try { await occurrenceException(ev, date, b.dataset.pid!, 'present'); toast('C’est noté : pour cette séance seulement, la règle reste'); reload(); }
    catch { toast('Enregistrement impossible.', 'ko'); }
  });
  for (const b of $$<HTMLButtonElement>(el, '[data-week-back]')) b.addEventListener('click', async () => {
    const [ev, date] = split(b.dataset.weekBack!);
    try { await setResponse(ev, date, b.dataset.pid!, 'present', null); toast('C’est noté !'); reload(); }
    catch { toast('Enregistrement impossible.', 'ko'); }
  });
}


// =================================================================================================================
// Le coach : « Faire le point avec … ? » (accueil), la séance (« prévenu tard », « sans prévenir »)
// =================================================================================================================
export async function renderPointCards(el: HTMLElement): Promise<void> {
  const list = await absencePoints();
  mount(el, html`${list.map(pointCard)}`);
  for (const card of $$<HTMLElement>(el, '[data-point]')) {
    const p = list.find(x => `${x.team_id}/${x.person_id}` === card.dataset.point)!;
    $(card, '[data-ds-contact]')?.addEventListener('click', () => void openContact(p));
    for (const [sel, action, msg] of [['[data-ds-done]', 'done', 'C’est noté. La carte revient au prochain désistement tardif.'], ['[data-ds-later]', 'later', 'La carte revient demain.']] as const) {
      $(card, sel)?.addEventListener('click', async () => {
        try { await absencePointDone(p.team_id, p.person_id, action); toast(msg); await renderPointCards(el); }
        catch { toast('Impossible pour le moment.', 'ko'); }
      });
    }
  }
}

function pointCard(p: PointCard): Html {
  const lates = p.history.filter(h => h.kind === 'late');
  const shows = p.history.filter(h => h.kind === 'no_show');
  const last = lates.at(-1);
  const g = p.gender;
  return html`
    <section class="card ds-point" data-guide="ds-point" data-point="${p.team_id}/${p.person_id}">
      <div class="ds-point-h"><span class="mi-ico" aria-hidden="true"><span class="ti ti-hand"></span></span>
        <span class="card-main"><span class="eyebrow eyebrow-accent">À faire · ${p.team}</span><span class="card-title">Faire le point avec ${p.name} ?</span></span></div>
      <p>${pointLead(p.count)}</p>
      ${lates.length ? html`<ol class="ds-hist">${lates.map(h => html`<li class="${h === last ? 'now' : ''}"><b>${h.day}</b>
        <span>${agree(p.pgender ?? p.gender, 'prévenu', 'prévenue')} à ${h.declared_hm}${h.reason_label ? ` · ${h.reason_label}` : ''}${h.word ? ` · « ${h.word} »` : ''}</span></li>`)}</ol>` : ''}
      ${shows.length ? html`<p class="muted small">${lates.length ? 'Et ' : ''}${shows.length > 1 ? `${shows.length} absences sans prévenir :` : `${lates.length ? 'une' : 'Une'} absence sans prévenir`} ${shows.map(h => `le ${h.day}${h.answered ? ` (réponse : ${h.reason_label ?? 'un mot'})` : ' (pas de réponse)'}`).join(', ')}.</p>` : ''}
      <div class="btn-row">
        <button type="button" class="btn btn-primary btn-sm" data-ds-contact>${agree(g, 'Le contacter', 'La contacter')}</button>
        <button type="button" class="btn btn-ghost btn-sm" data-ds-done>C’est fait</button>
        <button type="button" class="btn btn-link btn-sm" data-ds-later>Plus tard</button>
      </div>
    </section>`;
}

async function openContact(p: PointCard): Promise<void> {
  const c = await personContacts(p.person_id).catch(() => null);
  openSheet(`${agree(p.pgender ?? p.gender, 'Le contacter', 'La contacter')} · ${p.name}`, html`
    <p class="hint">Un mot en tête-à-tête suffit souvent : ${p.count} ${p.count > 1 ? 'désistements tardifs' : 'désistement tardif'} cette saison, rien d’automatique.</p>
    ${c ? contactsBlock(c, p.pgender ?? p.gender) : html`<p class="muted">Contacts indisponibles pour le moment.</p>`}`);
}

/** La séance du staff : les lignes « prévenu tard » et « sans prévenir » (maquettes d2, e1, e4). */
export const boardWhy = (n: BoardNotice): Html => (n.kind === 'late'
  ? html`${agree(n.gender, 'Absent', 'Absente')} · <span class="ds-late">${lateTag(n.gender)}</span>${n.reason_label ? ` · ${n.reason_label}` : ''}`
  : html`${agree(n.gender, 'Absent', 'Absente')} · <span class="ds-ns">sans prévenir</span>${n.reason_label ? ` · ${n.reason_label}` : ''}`);

export function boardSub(n: BoardNotice, team: TeamGender): Html {
  const g = n.gender ?? team;   // la personne : sa fiche, sinon son équipe
  const parts: Html[] = [];
  if (n.word) parts.push(html`« ${n.word} »`);
  const sick = n.reason === 'sick';
  const count = n.excused ? html`ne compte pas · <button type="button" class="ds-link" data-ds-excuse="${n.id}" data-v="0">Compter</button>`
    : sick ? html`ne compte pas (${agree(g, 'malade ou blessé', 'malade ou blessée')})`
    : html`${n.kind === 'late' && n.rank ? `${ordinal(n.rank)} de la saison · ` : ''}<button type="button" class="ds-link" data-ds-excuse="${n.id}" data-v="1">Ne pas compter</button>`;
  if (n.kind === 'no_show' && !n.answered_at) {
    const who = n.minor ? `${agree(g, 'il', 'elle')} et ses parents recevront` : `${agree(g, 'il', 'elle')} recevra`;
    if (n.asked_at) parts.push(html`Message envoyé à la fin de la séance, pas encore de réponse`);
    else if (!n.ask) parts.push(html`Sans nouvelles · pas de message (réglage de l’équipe)`);
    else if (!n.reachable) parts.push(html`Sans nouvelles · personne à prévenir dans l’app`);
    else if (n.no_message) parts.push(html`Sans nouvelles · pas de message. <button type="button" class="ds-link" data-ds-quiet="${n.id}" data-v="0">Lui écrire</button>`);
    else parts.push(html`Sans nouvelles : à ${n.ends_hm} (fin de la séance), ${who} un message pour expliquer. <button type="button" class="ds-link" data-ds-quiet="${n.id}" data-v="1">Ne pas lui écrire</button>`);
  }
  if (n.kind === 'late' || n.answered_at || n.asked_at) parts.push(count);
  return html`${parts.map((p, i) => html`${i ? ' · ' : ''}${p}`)}`;
}

/** En tête de la feuille : « Prévenue tard : Sarah, à 16h41 (la limite était mardi 20h00). », « Sans prévenir : Sarah a répondu à 21h40 · Transport. » */
export function boardTop(list: BoardNotice[]): Html {
  const late = list.filter(n => n.kind === 'late');
  const answered = list.filter(n => n.kind === 'no_show' && n.answered_at);
  return html`
    ${late.length ? html`<p class="ix-off">${late.length > 1 ? (late.every(n => n.gender === 'F') ? 'Prévenues tard' : 'Prévenus tard') : cap(lateTag(late[0]!.gender))} : ${late.map((n, i) => html`${i ? ' · ' : ''}<b>${n.name}</b>, à ${n.declared_hm}`)}${late[0]!.deadline_label ? ` (la limite était ${late[0]!.deadline_label})` : ''}.</p>` : ''}
    ${answered.length ? html`<p class="ix-off">Sans prévenir : ${answered.map((n, i) => html`${i ? ' · ' : ''}<b>${n.name}</b> a répondu à ${n.answered_hm}${n.reason_label ? ` · ${n.reason_label}` : ''}`)}.</p>` : ''}`;
}

export function wireBoard(root: HTMLElement, reload: () => void | Promise<void>): void {
  for (const b of $$<HTMLButtonElement>(root, '[data-ds-excuse]')) b.addEventListener('click', async () => {
    try { await absenceExcuse(b.dataset.dsExcuse!, b.dataset.v === '1'); toast(b.dataset.v === '1' ? 'C’est noté : il ne compte pas' : 'Il compte à nouveau'); await reload(); }
    catch { toast('Impossible pour le moment.', 'ko'); }
  });
  for (const b of $$<HTMLButtonElement>(root, '[data-ds-quiet]')) b.addEventListener('click', async () => {
    try { await absenceQuiet(b.dataset.dsQuiet!, b.dataset.v === '1'); toast(b.dataset.v === '1' ? 'Pas de message' : 'Le message partira à la fin de la séance'); await reload(); }
    catch { toast('Impossible pour le moment.', 'ko'); }
  });
}

// =================================================================================================================
// Le bureau : le taux de « prévenu à temps » par équipe (Club › Équipes et coachs)
// =================================================================================================================
export async function decorateTeamRates(root: HTMLElement, clubId: string): Promise<void> {
  const rates = await absenceClubRates(clubId);
  for (const r of rates) {
    if (r.rate === null || !r.total) continue;
    const a = $<HTMLElement>(root, `a.team-main[href="#/t/${r.team_id}"]`);
    if (!a || $(a, '.ds-rate')) continue;
    a.insertAdjacentHTML('beforeend', html`<span class="muted small ds-rate">Prévenu à temps : ${r.rate} % des absences (${r.total})</span>`.s);
  }
}
